import type { Types } from '@amplitude/analytics-browser';

import { redactUrlsInPayload } from '../redactSensitiveUrl';

/**
 * Sourced from `@amplitude/analytics-browser` rather than from
 * `@amplitude/analytics-types` directly: `packages/studio` declares its own
 * `@amplitude/analytics-types@^1.4.0`, so a direct import resolves to v1 while
 * `ampli.client.add()` is typed against the v2 types the browser SDK pulls in.
 * Going through the SDK's own `Types` re-export keeps us on whichever version
 * the client actually uses.
 */
type AmplitudeEnrichmentPlugin = Types.EnrichmentPlugin<
  Types.BrowserClient,
  Types.BrowserConfig
>;

/**
 * Amplitude enrichment plugin that removes secret tokens from any URL captured
 * on an event before it is sent to `api2.amplitude.com`.
 *
 * Amplitude's page-view, autocapture and Session Replay plugins attach the full
 * `window.location.href` and `document.referrer`, which on `/reset-password`,
 * `/verify-email` and `/org/:orgId/invite/:joinToken` contains a live
 * account-takeover token. See OE-1393.
 *
 * The whole event is walked rather than a hand-kept list of fields. The URL
 * does not appear at the top level of an Amplitude v2 event at all: the
 * page-view plugin puts it in `event_properties` under vendor-defined keys
 * (`[Amplitude] Page URL`, `[Amplitude] Page Location`, `[Amplitude] Page
 * Path`), and attribution puts the referrer in `user_properties`. Walking the
 * event also covers `group_properties`, `groups` and `extra`, and does not
 * need updating when the SDK moves a field.
 *
 * Fails closed: returning `null` makes Amplitude drop an event we could not redact.
 */
export const redactSensitiveUrlsPlugin = (): AmplitudeEnrichmentPlugin => ({
  name: 'redact-sensitive-urls',
  type: 'enrichment',
  setup: async () => undefined,
  execute: async (event) => {
    try {
      return redactUrlsInPayload(event);
    } catch {
      return null;
    }
  },
});
